import { useTranslation } from 'react-i18next';

import { UnsupportedContainer, UnsupportedTitle, UnsupportedDescription } from './index.styles';
import { MetricCardRenderer } from './renderers/MetricCardRenderer';
import { TimeSeriesRenderer } from './renderers/TimeSeriesRenderer';
import { PieChartRenderer } from './renderers/PieChartRenderer';
import { RankedTableRenderer } from './renderers/RankedTableRenderer';
import { TableRenderer } from './renderers/TableRenderer';
import { ScatterRenderer } from './renderers/ScatterRenderer';
import { HeatMapRenderer } from './renderers/HeatMapRenderer';
import { PostsCardListRenderer } from './renderers/PostsCardListRenderer';

// ── Chart type → renderer mapping ──

const chartRenderers: Record<ChartType, React.FC<DynamicModuleBuilderProps>> = {
  MetricCard: MetricCardRenderer,
  Line: TimeSeriesRenderer,
  Bar: TimeSeriesRenderer,
  Area: TimeSeriesRenderer,
  Pie: PieChartRenderer,
  RankedTable: RankedTableRenderer,
  SummaryTable: TableRenderer,
  Scatter: ScatterRenderer,
  HeatMap: HeatMapRenderer,
  PostsCardList: PostsCardListRenderer,
};

// ── Dynamic Module Builder ──

export const DynamicModuleBuilder = ({
  module,
  data,
  isLoading,
  selectedProfiles,
  supportedPlatforms,
  onFetchNextPage,
  hasNextPage,
}: DynamicModuleBuilderProps & {
  selectedProfiles?: ReportProfile[];
  supportedPlatforms?: string[];
}) => {
  const { t } = useTranslation('reports');

  if (!data) {
    return (
      <UnsupportedContainer>
        <UnsupportedDescription>{t('new_reports_no_engagement_data')}</UnsupportedDescription>
      </UnsupportedContainer>
    );
  }

  const Renderer = chartRenderers[data.chartType];

  if (!Renderer) {
    return (
      <UnsupportedContainer>
        <UnsupportedTitle>{t('new_reports_unsupported_module_title')}</UnsupportedTitle>
        <UnsupportedDescription>{t('new_reports_unsupported_chart_type', { chartType: data.chartType })}</UnsupportedDescription>
      </UnsupportedContainer>
    );
  }

  return (
    <Renderer
      module={module}
      data={data}
      isLoading={isLoading}
      selectedProfiles={selectedProfiles}
      supportedPlatforms={supportedPlatforms}
      onFetchNextPage={onFetchNextPage}
      hasNextPage={hasNextPage}
    />
  );
};
